import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import * as nav_const from '../../app-navigation';
import { environment } from 'src/environments/environment';
import { FeatureFlagsService } from './featureFlags.service';
import { UserService } from './user/user.service';
import { CatalogConfigurationsService } from './catalog-configurations.service';

export interface MenuItem {
  text?: string;
  path?: string;
  externalUrl?: string;
  icon?: string;
  items?: MenuItem[];
  expanded?: boolean;
  template?: string;
}

@Injectable({
  providedIn: 'root',
})
export class MenuService {
  private menuItemsSubject = new BehaviorSubject<MenuItem[]>([]);
  menuItems$ = this.menuItemsSubject.asObservable();

  private userInfoSubject = new BehaviorSubject<{ user: any; medigiAdmin: boolean } | null>(null);
  userInfo$ = this.userInfoSubject.asObservable();

  user: any;
  client: any;
  medigiAdmin = false;
  rfpEnabled = true;
  bidEnabled = true;

  cartTemplateEnabled = this.featureFlagsService.get('cart_template');
  cartTemplateScheduleEnabled = this.featureFlagsService.get('cart_template_schedule');
  coaSearchEnabled = this.featureFlagsService.get('coa_menu_item');

  constructor(
    private userService: UserService,
    private featureFlagsService: FeatureFlagsService,
    private catalogConfigsService: CatalogConfigurationsService
  ) {}

  updateMenu(menuOpened: boolean = false) {
    this.userService.getMe().subscribe(user => {
      this.user = user.data;
      this.medigiAdmin = this.user.email.includes('admin@medigi');
      this.userInfoSubject.next({ user: this.user, medigiAdmin: this.medigiAdmin });
      
      let items: MenuItem[] = [];

      if (!environment.hydraEnabled) {
        items = this.getMedigiItems(menuOpened);
      } else {
        this.getHydraItems(menuOpened).then(hydraItems => {
          this.menuItemsSubject.next(hydraItems);
        });
        return;
      }
      
      this.menuItemsSubject.next(items);
    });
  }

  private getMedigiItems(menuOpened: boolean): MenuItem[] {
    let items: MenuItem[] = [];
    
    if (this.user.is_sales_representative) {
      return [...nav_const.sales_representative];
    }
    
    if (this.user.is_customer_representative) {
      return [...nav_const.sales_representative];
    }

    if (this.user.roles.includes('Buyer')) {
      items = this.addBuyerItems(items, menuOpened);
    }

    if (this.user.roles.includes('Seller')) {
      items = this.addSellerItems(items, menuOpened);
    }

    if (this.user.roles.includes('Administrator')) {
      items = items.concat(nav_const.admin);
      if (this.coaSearchEnabled) {
        items = items.concat(nav_const.COASearch);
      }
    }

    if (this.user.roles.includes('Buyer')) {
      items = items.concat(nav_const.reports);
    }

    items = items.concat(nav_const.lowerMenu);
    return items;
  }

  private async getHydraItems(menuOpened: boolean): Promise<MenuItem[]> {
    this.client = await this.userService._getMsClient();
    this.rfpEnabled = this.client?.hydra_config?.rfp_enabled;
    this.bidEnabled = this.client?.hydra_config?.bid_enabled;

    let items: MenuItem[] = [];

    if (this.user.roles.includes('Buyer')) {
      items = this.getHydraBuyerItems(items, menuOpened);
    }

    if (this.user.roles.includes('Seller')) {
      items = this.getHydraSellerItems(items, menuOpened);
    }

    if (this.user.roles.includes('Administrator')) {
      items = items.concat(nav_const.admin);
      if (this.coaSearchEnabled) {
        items = items.concat(nav_const.COASearch);
      }
    }

    items = items.concat(nav_const.lowerMenu);
    return items;
  }

  private addBuyerItems(items: MenuItem[], menuOpened: boolean): MenuItem[] {
    let buyerItems = [...items];
    if (location.host === environment.clientDomains.medshortsDomain) {
      buyerItems = buyerItems.concat(nav_const.medigi);
    }

    buyerItems = buyerItems.concat(nav_const.buyer);
    buyerItems = this.setBuyerSideCatalogMenu(buyerItems, 'Catalogs', menuOpened);
    buyerItems = buyerItems.concat(nav_const.bids);

    if (window.location.host !== environment.clientDomains.medshortsDomain) {
      buyerItems = buyerItems.concat(nav_const.rfps);
    }
    buyerItems = buyerItems.concat(nav_const.buyer_requests);
    
    if (this.cartTemplateEnabled) {
      buyerItems = buyerItems.concat(nav_const.cart_template);
    }
    if (this.cartTemplateScheduleEnabled) {
      buyerItems = buyerItems.concat(nav_const.cart_schedule);
    }
    if (this.user.roles.includes('CSOS Signer')) {
      buyerItems = buyerItems.concat(nav_const.csos);
    }

    if (this.user.roles.includes('CompanyAdmin') || this.user.roles.includes('FacilityAdmin')) {
      buyerItems = buyerItems.concat(nav_const.buyer_editing);
    }
    return buyerItems;
  }

  private addSellerItems(items: MenuItem[], menuOpened: boolean): MenuItem[] {
    let sellerItems = [...items].concat(nav_const.seller_home);

    if (this.user.company.is_503b) {
      sellerItems = sellerItems.concat(nav_const.seller_listings_503b);
    } else {
      sellerItems = sellerItems.concat(nav_const.seller_listings);
    }

    sellerItems = sellerItems.concat(nav_const.seller_default);
    sellerItems = this.setSellerSideListingMenu(sellerItems, menuOpened);

    if (!this.user.is_field_rep) {
      sellerItems = sellerItems.concat(nav_const.reports);
    }

    if (this.user.roles.includes('CompanyAdmin')) {
      sellerItems = sellerItems.concat(nav_const.seller_admin);
    }
    sellerItems = sellerItems.concat(nav_const.seller_guides);
    return sellerItems;
  }

  private getHydraBuyerItems(items: MenuItem[], menuOpened: boolean): MenuItem[] {
    let buyerItems = [...items];
    if (this.user.is_sales_representative) {
      return [...nav_const.sales_representative];
    }

    if (this.user.is_customer_representative) {
      return [...nav_const.sales_representative];
    }

    buyerItems = buyerItems.concat(nav_const.hydra_buyer);
    buyerItems = this.setBuyerSideCatalogMenu(buyerItems, 'Catalog', menuOpened);

    if (this.bidEnabled) {
      buyerItems = buyerItems.concat(nav_const.bids);
    }
    if (this.rfpEnabled) {
      buyerItems = buyerItems.concat(nav_const.rfps);
    }
    if (this.cartTemplateEnabled) {
      buyerItems = buyerItems.concat(nav_const.cart_template);
    }
    if (this.cartTemplateScheduleEnabled) {
      buyerItems = buyerItems.concat(nav_const.cart_schedule);
    }
    if (this.user.roles.includes('CSOS Signer')) {
      buyerItems = buyerItems.concat(nav_const.csos);
    }
    if (this.user.roles.includes('CompanyAdmin') || this.user.roles.includes('FacilityAdmin')) {
      buyerItems = buyerItems.concat(nav_const.buyer_editing);
    }
    buyerItems = buyerItems.concat(nav_const.reports_only);

    if (this.coaSearchEnabled) {
      buyerItems = buyerItems.concat(nav_const.COASearch);
    }
    return buyerItems;
  }

  private getHydraSellerItems(items: MenuItem[], menuOpened: boolean): MenuItem[] {
    let sellerItems = [...items].concat(nav_const.seller_hydra_listings);
    sellerItems = sellerItems.concat(nav_const.seller_hydra_default);

    if (this.bidEnabled) {
      sellerItems = sellerItems.concat(nav_const.bids);
    }
    if (this.rfpEnabled) {
      sellerItems = sellerItems.concat(nav_const.rfps);
    }

    sellerItems = sellerItems.concat(nav_const.quicksight_only);

    if (this.user.is_admin) {
      sellerItems = sellerItems.concat(nav_const.hydra_admin);
      if (this.cartTemplateEnabled) {
        sellerItems = sellerItems.concat(nav_const.cart_template);
      }
      if (this.cartTemplateScheduleEnabled) {
        sellerItems = sellerItems.concat(nav_const.cart_schedule);
      }
    }
    sellerItems = sellerItems.concat(nav_const.reports_only);
    if (this.coaSearchEnabled) {
      sellerItems = sellerItems.concat(nav_const.COASearch);
    }
    sellerItems = this.setSellerSideListingMenu(sellerItems, menuOpened);

    return sellerItems;
  }

  private setBuyerSideCatalogMenu(items: MenuItem[], term: string, menuOpened: boolean): MenuItem[] {
    const buyerSideCatalogMenu = this.featureFlagsService.get('buyer_side_catalog_menu');
    if (buyerSideCatalogMenu && buyerSideCatalogMenu[environment.supplierConfig.clientNameSpace]) {
      const insertIndex = items.findIndex(item => item.text === term);
      if (insertIndex !== -1) {
        const allItemsMenu = {
          text: 'All Items',
          path: '/catalog',
          icon: 'catalogs',
        };
        items[insertIndex] = {
          text: term,
          icon: 'catalogs',
          items: [
            allItemsMenu,
            ...buyerSideCatalogMenu[environment.supplierConfig.clientNameSpace],
          ],
          expanded: !!menuOpened,
        };
      }
    }
    return items;
  }

  private setSellerSideListingMenu(items: MenuItem[], menuOpened: boolean): MenuItem[] {
    const sellerSideListingMenu = this.featureFlagsService.get('seller_side_listing_menu');
    if (sellerSideListingMenu && sellerSideListingMenu[environment.supplierConfig.clientNameSpace]) {
      const insertIndex = items.findIndex(item => item.text === 'Listings');
      if (insertIndex !== -1) {
        items[insertIndex] = {
          text: 'Listings',
          icon: 'listings',
          items: [...sellerSideListingMenu[environment.supplierConfig.clientNameSpace]],
          expanded: !!menuOpened,
        };
      }
    }
    return items;
  }
}
